"use client";
import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { useVisual } from "../../context/VisualContext";
import { useUser } from "../../context/UserContext";
import { useHandleStreamResponse } from "../../hooks/useEffects";
import ParticleSystem from "../ui/ParticleSystem";

const ChatInterface = () => {
  const [messages, setMessages] = useState([
    {
      type: "ai",
      content: "Hello! I'm your AI assistant. How can I help you today?",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const { isLoading, setLoading, addNotification } = useApp();
  const { settings: visualSettings } = useVisual();
  const { isAuthenticated } = useUser();
  const { streamResponse, isStreaming, streamedContent, resetStream } = useHandleStreamResponse();
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages]);

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Handle message submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;
    
    // Add user message
    const userMessage = {
      type: "user",
      content: inputValue,
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    
    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    try {
      // Show typing indicator
      setMessages(prev => [...prev, { type: "typing" }]);
      
      // Get AI response
      const response = await streamResponse(
        '/integrations/chat-gpt/conversationgpt4',
        { message: userMessage.content }
      );
      
      // Remove typing indicator and add AI response
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.type !== "typing");
        return [...filtered, { type: "ai", content: response }];
      });
      
      resetStream();
    } catch (error) {
      console.error("Error getting AI response:", error);
      
      // Remove typing indicator
      setMessages(prev => prev.filter(msg => msg.type !== "typing"));
      
      // Add error message
      setMessages(prev => [
        ...prev,
        { type: "error", content: "Sorry, I couldn't process your request. Please try again." }
      ]);
      
      addNotification("Failed to get AI response", "error");
    }
  };

  // Export chat history
  const exportChat = () => {
    try {
      const chatData = JSON.stringify(messages, null, 2);
      const blob = new Blob([chatData], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `chat_export_${new Date().toISOString()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      addNotification("Chat history exported successfully", "success");
    } catch (error) {
      console.error("Export error:", error);
      addNotification("Failed to export chat history", "error");
    }
  };

  // Render message content
  const renderMessageContent = (message) => {
    if (message.type === "typing") {
      return (
        <div className="flex space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-75"></div>
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse delay-150"></div>
        </div>
      );
    }
    
    if (message.type === "error") {
      return <div className="text-red-400">{message.content}</div>;
    }
    
    return message.content;
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-cosmic-dark to-cosmic-medium">
      <div className="max-w-4xl mx-auto p-4 h-screen flex flex-col relative">
        {/* Particle background */}
        <div className="absolute inset-0 z-0">
          <ParticleSystem 
            density={visualSettings.particleDensity} 
            colorScheme={visualSettings.colorScheme}
            interactive={true}
          />
        </div>
        
        <div className="cosmic-card flex-1 flex flex-col z-10">
          <div className={`p-4 flex justify-between items-center ${
            visualSettings.colorScheme === 'nebula' ? 'bg-nebula-gradient' :
            visualSettings.colorScheme === 'cosmic' ? 'bg-cosmic-gradient' :
            'bg-aurora-gradient'
          }`}>
            <h1 className="text-xl font-semibold">AI Assistant</h1>
            <button
              onClick={exportChat}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              title="Export chat history"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          <div 
            ref={chatWindowRef}
            className="flex-1 overflow-y-auto p-4 space-y-4"
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl p-3 ${
                    message.type === "user"
                      ? "bg-nebula-primary text-white"
                      : message.type === "ai"
                      ? "bg-cosmic-medium bg-opacity-80"
                      : "bg-transparent"
                  }`}
                >
                  {renderMessageContent(message)}
                </div>
              </div>
            ))}
            
            {isStreaming && streamedContent && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl p-3 bg-cosmic-medium bg-opacity-80">
                  {streamedContent}
                </div>
              </div>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700">
            <div className="flex space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder="Type your message..."
                className="cosmic-input flex-1"
                disabled={isStreaming}
              />
              <button
                type="submit"
                className={`cosmic-button ${
                  visualSettings.colorScheme === 'nebula' ? 'bg-nebula-gradient' :
                  visualSettings.colorScheme === 'cosmic' ? 'bg-cosmic-gradient' :
                  'bg-aurora-gradient'
                }`}
                disabled={isStreaming || !inputValue.trim()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
};

export default ChatInterface;
